import { Action } from '../../actions';
import {
    ON_ACCEPT_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR,
    OnAcceptMassActionsTurbolandingBulkReplacementEditor
} from '../../actions/banners-edit/banners-edit-mass-actions';
import {
    CHANGE_CONTACTS_DESKTOP_TYPE_EDIT_BANNERS,
    CHANGE_CONTACTS_MOBILE_TYPE_EDIT_BANNERS,
    CHANGE_MAIN_LINK_PROTOCOL,
    ChangeContactsDesktopTypeEditBannersAction,
    ChangeContactsMobileTypeEditBannersAction,
    ChangeMainLinkProtocol,
    CHECK_BANNER_HREF_STATUS,
    CheckBannerHrefStatus,
    CLEAR_MAIN_LINK_DOMAIN,
    ClearMainLinkDomain,
    SHOW_BANNERS_CONTACTS_VALIDATION_ERROR,
    ShowBannersContactsValidationError,
    UPDATE_BANNER_CONTACTS_FOR_USER,
    UPDATE_BANNER_CONTACTS_FOR_USER_DOMAIN,
    UPDATE_BANNER_DISPLAY_DOMAIN,
    UPDATE_BANNER_HREF_STATUS,
    UPDATE_HREF_BANNER_CONTACTS_FOR_USER,
    UPDATE_MAIN_LINK,
    UpdateBannerContactsForUser,
    UpdateBannerContactsForUserDomain,
    UpdateBannerDisplayDomain,
    UpdateBannerHrefStatus,
    UpdateHrefBannerContactsForUser,
    UpdateMainLink
} from '../../actions/banners-edit/contacts';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BannerId } from '../../typings/banners';
import {
    EditBannersState,
    EditedBanner,
    getEditedBannerWithFullContacts,
    ProtocolValue
} from '../../typings/bannersEditState';
import { PageStatus } from '../../typings/common';
import { ContactsEditorType } from '../../typings/domain';
import { State } from '../../typings/state';

export function contactsReducer(
    state: EditBannersState,
    action: Action,
    fullState: State
): EditBannersState {
    switch (action.type) {
        case UPDATE_HREF_BANNER_CONTACTS_FOR_USER:
            return updateHrefContactsForUser(state, action);
        case UPDATE_BANNER_CONTACTS_FOR_USER:
        case UPDATE_BANNER_CONTACTS_FOR_USER_DOMAIN:
            return updateContactsForUser(state, action);
        case CHECK_BANNER_HREF_STATUS:
            return checkHrefStatus(state, action);
        case UPDATE_BANNER_HREF_STATUS:
            return updateHrefStatus(state, action);
        case UPDATE_MAIN_LINK:
            return updateMainLink(state, action);
        case CHANGE_MAIN_LINK_PROTOCOL:
            return updateMainLinkProtocol(state, action);
        case UPDATE_BANNER_DISPLAY_DOMAIN:
            return updateDisplayDomain(state, action);
        case CLEAR_MAIN_LINK_DOMAIN:
            return clearMainLinkDomain(state, action);
        case ON_ACCEPT_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR:
            return changeTurboFromMassActions(state, action);
        case CHANGE_CONTACTS_DESKTOP_TYPE_EDIT_BANNERS:
            return changeContactsDesktopTypeEditBanners(state, action);
        case CHANGE_CONTACTS_MOBILE_TYPE_EDIT_BANNERS:
            return changeContactsMobileTypeEditBanners(state, action);
        case SHOW_BANNERS_CONTACTS_VALIDATION_ERROR:
            return showBannersContactsValidationError(state, action);
        default:
            return state;
    }
}

const showBannersContactsValidationError = (
    state: EditBannersState,
    action: ShowBannersContactsValidationError
): EditBannersState => {
    const { error } = action;

    const newBanners = Object.keys(error).reduce((acc, val) => {
        const banner = state.banners[val];
        const bannerError = error[val];

        acc[val] = banner;

        if (error[val]) {
            acc[val] = {
                ...banner,
                errors: {
                    errors: bannerError
                }
            };
        }

        return acc;
    }, {});

    return {
        ...state,
        banners: {
            ...state.banners,
            ...newBanners
        },
        pageStatus: PageStatus.LOADED
    };
};

const changeContactsMobileTypeEditBanners = (
    state: EditBannersState,
    action: ChangeContactsMobileTypeEditBannersAction
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    mobileType: action.val
                },
                errors: {
                    ...banner.errors,
                    href: {
                        errors: []
                    }
                }
            }
        }
    };
};

const changeTurboFromMassActions = (
    state: EditBannersState,
    action: OnAcceptMassActionsTurbolandingBulkReplacementEditor
): EditBannersState => {
    const banners = Object.keys(state.banners).reduce(
        (acc, bannerId) => {
            const banner = getEditedBannerWithFullContacts(
                state.banners[bannerId]
            );

            if (!banner) {
                return { ...acc, [bannerId]: state.banners[bannerId] };
            }

            return {
                ...acc,
                [bannerId]: {
                    ...banner,
                    contacts: {
                        ...banner.contacts,
                        mobileType:
                            banner.contacts.mobileType ===
                            ContactsEditorType.LINK
                                ? ContactsEditorType.TURBO
                                : banner.contacts.mobileType
                    },
                    errors: {
                        ...banner.errors,
                        href: {
                            errors: []
                        }
                    }
                }
            };
        },
        {} as Record<BannerId, EditedBanner>
    );

    return {
        ...state,
        banners
    };
};

const changeContactsDesktopTypeEditBanners = (
    state: EditBannersState,
    action: ChangeContactsDesktopTypeEditBannersAction
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner || banner.contacts.desktopAndTabletType === action.val) {
        return state;
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    desktopAndTabletType: action.val,
                    mobileType:
                        action.val !== ContactsEditorType.LINK
                            ? action.val
                            : ContactsEditorType.LINK
                },
                errors: {
                    ...banner.errors,
                    href: {
                        errors: []
                    }
                }
            }
        }
    };
};

const updateHrefContactsForUser = (
    state: EditBannersState,
    action: UpdateHrefBannerContactsForUser
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }

    if (banner.contacts.isDomainCleared) {
        return {
            ...state,
            banners: {
                ...state.banners,
                [action.bannerId]: {
                    ...banner,
                    contacts: {
                        ...banner.contacts,
                        isDomainCleared: false
                    }
                }
            }
        };
    }

    const href = action.hrefValue || '';
    const formattedHref = UrlHelpers.deleteProtocol(href);

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    href: formattedHref,
                    isUrlChecking: false
                },
                errors: {
                    ...banner.errors,
                    href: {
                        errors: []
                    }
                }
            }
        }
    };
};

const updateContactsForUser = (
    state: EditBannersState,
    action: UpdateBannerContactsForUser | UpdateBannerContactsForUserDomain
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }

    if (banner.contacts.isDomainCleared) {
        return {
            ...state,
            banners: {
                ...state.banners,
                [action.bannerId]: {
                    ...banner,
                    contacts: {
                        ...banner.contacts,
                        isDomainCleared: false
                    }
                }
            }
        };
    }

    const contactsForUser = action.contactsForUser;
    const href = contactsForUser.href.value || '';
    const hrefProtocol =
        (UrlHelpers.extractProtocol(href) as ProtocolValue) ||
        contactsForUser.hrefProtocol;
    const formattedHref = UrlHelpers.deleteProtocol(href);

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    href: formattedHref,
                    hrefProtocol,
                    isUrlChecking: false,
                    displayHref: contactsForUser.displayHref.value,
                    domain: contactsForUser.displayDomain.value
                },
                errors: {
                    ...banner.errors,
                    href: {
                        errors: []
                    }
                }
            }
        }
    };
};

const checkHrefStatus = (
    state: EditBannersState,
    action: CheckBannerHrefStatus
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }
    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    isUrlChecking: true
                }
            }
        }
    };
};

const updateHrefStatus = (
    state: EditBannersState,
    action: UpdateBannerHrefStatus
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }
    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    isUrlChecking: false
                },
                errors: !action.isValid
                    ? {
                          href: {
                              errors: [
                                  {
                                      text: action.text
                                  }
                              ]
                          }
                      }
                    : {}
            }
        }
    };
};

const updateMainLink = (
    state: EditBannersState,
    action: UpdateMainLink
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }
    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    href: action.href || ''
                },
                errors: {
                    ...banner.errors,
                    href: {
                        errors: []
                    }
                }
            }
        }
    };
};

const updateMainLinkProtocol = (
    state: EditBannersState,
    action: ChangeMainLinkProtocol
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    hrefProtocol: action.value
                },
                errors: {
                    ...banner.errors,
                    href: {
                        errors: []
                    }
                }
            }
        }
    };
};

const clearMainLinkDomain = (
    state: EditBannersState,
    action: ClearMainLinkDomain
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    isDomainCleared: true,
                    domain: ''
                }
            }
        }
    };
};

const updateDisplayDomain = (
    state: EditBannersState,
    action: UpdateBannerDisplayDomain
): EditBannersState => {
    const banner = getEditedBannerWithFullContacts(
        state.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                contacts: {
                    ...banner.contacts,
                    domain: action.domain
                }
            }
        }
    };
};
